import { useEffect, useState, useCallback } from "react";
import { Download, Plus, Trash2, Pencil, Check, X, Bell, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const STATUS = ["pending", "paid", "overdue"];
const TYPES = ["quotation", "supply", "purchase"];
const color = { paid: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", overdue: "bg-red-100 text-red-700" };
const EMPTY = { client_id: "", client_name: "", client_address: "", type: "quotation", status: "pending", due_date: "", installation_charge: "", transportation_charge: "" };
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [rows, setRows] = useState([{ name: "", price: "", qty: 1 }]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reminding, setReminding] = useState(null);
  const [remindingAll, setRemindingAll] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);

  const load = useCallback(() => { api.get("/invoices").then((r) => setInvoices(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin) api.get("/users?role=client").then((r) => setClients(r.data)).catch(() => {});
  }, [isAdmin]);

  // Prefill from an enquiry ("Create Quotation")
  useEffect(() => {
    const raw = sessionStorage.getItem("sf_quote_prefill");
    if (raw && isAdmin) {
      try {
        const p = JSON.parse(raw);
        setEditId(null);
        setForm({ ...EMPTY, client_id: p.client_id || "", client_name: p.client_name || "" });
        setRows((p.items || []).map((it) => ({ name: it.name, price: "", qty: it.qty || 1 })));
        setOpen(true);
      } catch (e) { console.error("Failed to parse quote prefill", e); }
      sessionStorage.removeItem("sf_quote_prefill");
    }
  }, [isAdmin]);

  const changeStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    await api.patch(`/invoices/${id}/status`, fd);
    toast.success("Status updated"); load();
  };

  const download = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Could not open PDF"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success("Quotation deleted");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error("Could not delete quotation"); } finally { setDeleting(false); }
  };

  const respond = async (id, action, reason = "") => {
    const fd = new FormData(); fd.append("action", action); if (reason) fd.append("reason", reason);
    try {
      await api.patch(`/invoices/${id}/respond`, fd);
      toast.success(action === "accept" ? "Quotation accepted" : "Quotation rejected");
      load();
    } catch (e) { toast.error("Could not update response"); }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    await respond(rejectTarget.id, "reject", rejectReason.trim());
    setRejectTarget(null); setRejectReason("");
  };

  const remind = async (id) => {
    setReminding(id);
    try {
      await api.post(`/invoices/${id}/remind`);
      toast.success("Reminder sent to client");
    } catch (e) { toast.error("Could not send reminder"); } finally { setReminding(null); }
  };

  const remindAll = async () => {
    setRemindingAll(true);
    try {
      const { data } = await api.post("/invoices/remind-all");
      if (data.attempted === 0) toast.info("No pending quotations to remind right now");
      else toast.success(`Reminded ${data.sent} of ${data.attempted} pending quotation(s)`);
      load();
    } catch (e) { toast.error("Could not send bulk reminders"); } finally { setRemindingAll(false); }
  };

  const itemsSubtotal = rows.reduce((a, r) => a + (parseFloat(r.price) || 0) * (parseInt(r.qty) || 0), 0);
  const extras = (parseFloat(form.installation_charge) || 0) + (parseFloat(form.transportation_charge) || 0);
  const previewTotal = (itemsSubtotal + extras) * 1.18;

  const openNew = () => { setEditId(null); setForm(EMPTY); setRows([{ name: "", price: "", qty: 1 }]); setOpen(true); };

  const openEdit = (inv) => {
    setEditId(inv.id);
    setForm({
      client_id: inv.client_id || "", client_name: inv.client_name || "", client_address: inv.client_address || "",
      type: inv.type || "quotation", status: inv.status || "pending", due_date: (inv.due_date || "").slice(0, 10),
      installation_charge: inv.installation_charge || "", transportation_charge: inv.transportation_charge || "",
    });
    setRows((inv.items || []).map((it) => ({ name: it.name, price: it.price, qty: it.qty || 1, unit: it.unit || "", section: it.section || "" })));
    setOpen(true);
  };

  const pickClient = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm({ ...form, client_id: id, client_name: c ? (c.company || c.name) : form.client_name });
  };

  const save = async () => {
    const items = rows.filter((r) => r.name && r.price).map((r) => ({ product_id: "", name: r.name, price: parseFloat(r.price), qty: parseInt(r.qty) || 1, unit: r.unit || "Nos.", section: r.section || "" }));
    if (!form.client_name || items.length === 0) return toast.error("Add client name and at least one priced item");
    if (form.type === "quotation" && !form.client_id) return toast.error("Please select a client account so this quotation reaches their portal");
    const payload = {
      ...form, items, tax_rate: 18,
      installation_charge: parseFloat(form.installation_charge) || 0,
      transportation_charge: parseFloat(form.transportation_charge) || 0,
    };
    try {
      if (editId) {
        await api.put(`/invoices/${editId}`, payload);
        toast.success("Quotation updated");
      } else {
        await api.post("/invoices", payload);
        toast.success(form.type === "quotation" ? "Quotation created & emailed" : `${form.type[0].toUpperCase()}${form.type.slice(1)} invoice created`);
      }
      setOpen(false); setEditId(null);
      setForm(EMPTY); setRows([{ name: "", price: "", qty: 1 }]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (editId ? "Could not update quotation" : "Could not create quotation")); }
  };

  return (
    <div className="p-8">
      <PageHeader title="Quotations & Invoices" subtitle="Billing">
        {isAdmin && (
          <div className="flex gap-2">
            <Button onClick={remindAll} disabled={remindingAll} variant="outline" className="rounded-sm gap-2" data-testid="remind-all-btn"><Bell size={16} /> {remindingAll ? "Reminding…" : "Remind All Pending"}</Button>
            <Button onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="new-invoice-btn"><Plus size={16} /> New Quotation</Button>
          </div>
        )}
      </PageHeader>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <Table className="min-w-[720px]">
          <TableHeader><TableRow className="bg-muted/50">
            <TableHead>Number</TableHead><TableHead>Type</TableHead>
            {isAdmin && <TableHead>Client</TableHead>}
            <TableHead>Total</TableHead><TableHead>Status</TableHead>
            {!isAdmin && <TableHead>Your Response</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                <TableCell className="capitalize">{inv.type}</TableCell>
                {isAdmin && (
                  <TableCell>
                    {inv.client_name}
                    {inv.type === "quotation" && inv.client_status && inv.client_status !== "pending" && (
                      <div className={`text-xs mt-0.5 font-medium ${inv.client_status === "accepted" ? "text-green-600" : inv.client_status === "expired" ? "text-muted-foreground" : "text-red-600"}`} data-testid={`client-response-${inv.id}`}>
                        {inv.client_status === "expired" ? "Quotation expired" : `Client ${inv.client_status}`}
                        {inv.client_status === "rejected" && inv.reject_reason && (
                          <span className="block font-normal text-muted-foreground italic" data-testid={`reject-reason-${inv.id}`}>"{inv.reject_reason}"</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                )}
                <TableCell className="font-semibold">₹{inv.total.toLocaleString("en-IN")}</TableCell>
                <TableCell>
                  {isAdmin ? (
                    <Select value={inv.status} onValueChange={(v) => changeStatus(inv.id, v)}><SelectTrigger className="w-28 h-8 rounded-sm text-xs" data-testid={`inv-status-${inv.id}`}><SelectValue /></SelectTrigger><SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                  ) : inv.client_status === "expired" ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : <Badge className={`rounded-sm capitalize ${color[inv.status]}`}>{inv.status}</Badge>}
                </TableCell>
                {!isAdmin && (
                  <TableCell>
                    {inv.type !== "quotation" ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : inv.client_status === "accepted" ? (
                      <Badge className="rounded-sm bg-green-100 text-green-700" data-testid={`response-badge-${inv.id}`}>Accepted</Badge>
                    ) : inv.client_status === "rejected" ? (
                      <Badge className="rounded-sm bg-red-100 text-red-700" data-testid={`response-badge-${inv.id}`}>Rejected</Badge>
                    ) : inv.client_status === "expired" ? (
                      <Badge className="rounded-sm bg-slate-200 text-slate-600" data-testid={`response-badge-${inv.id}`}>Expired</Badge>
                    ) : (
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => respond(inv.id, "accept")} className="rounded-sm gap-1 border-green-600 text-green-700 hover:bg-green-50" data-testid={`accept-invoice-${inv.id}`}><Check size={14} /> Accept</Button>
                        <Button size="sm" variant="outline" onClick={() => { setRejectTarget(inv); setRejectReason(""); }} className="rounded-sm gap-1 border-red-600 text-red-700 hover:bg-red-50" data-testid={`reject-invoice-${inv.id}`}><X size={14} /> Reject</Button>
                      </div>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && inv.type === "quotation" && (inv.client_status || "pending") === "pending" && inv.client_email && (
                      <Button size="sm" variant="outline" onClick={() => remind(inv.id)} disabled={reminding === inv.id} className="rounded-sm gap-1" data-testid={`remind-invoice-${inv.id}`}><Bell size={14} /> {reminding === inv.id ? "Sending…" : "Remind"}</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setHistoryTarget(inv)} className="rounded-sm" data-testid={`history-invoice-${inv.id}`}><Clock size={14} /></Button>
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => openEdit(inv)} className="rounded-sm gap-1" data-testid={`edit-invoice-${inv.id}`}><Pencil size={14} /> Edit</Button>}
                    <Button size="sm" variant="outline" onClick={() => download(inv.id)} className="rounded-sm gap-1" data-testid={`download-invoice-${inv.id}`}><Download size={14} /> PDF</Button>
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => setDeleteTarget(inv)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid={`delete-invoice-${inv.id}`}><Trash2 size={14} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {invoices.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No quotations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Quotation" : "Create Quotation"}</DialogTitle>
            <DialogDescription>{editId ? "Update party details, line items and charges, then save." : "Fill in party details, line items and charges to generate a quotation."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {isAdmin && (
              <div>
                <Label className="text-xs uppercase">Client Account</Label>
                <Select value={form.client_id || "none"} onValueChange={(v) => pickClient(v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="inv-client-select"><SelectValue placeholder="Select a client…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No linked account (walk-in) —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Required for quotations, so it appears in the client's portal for review.</p>
              </div>
            )}
            <div><Label className="text-xs uppercase">Party Name</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="rounded-sm mt-1" data-testid="inv-client" /></div>
            <div><Label className="text-xs uppercase">Party Address</Label><Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} placeholder="City, State" className="rounded-sm mt-1" data-testid="inv-address" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs uppercase">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger><SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs uppercase">Due</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm mt-1" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase">Line Items (with your price)</Label>
              <div className="space-y-2 mt-1">
                {rows.map((r, i) => (
                  <div key={i} className="flex flex-wrap gap-2 items-center border-b border-dashed border-border pb-2">
                    <Input placeholder="Section (e.g. Fire Alarm)" value={r.section || ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, section: e.target.value } : x))} className="rounded-sm w-full text-xs" data-testid={`inv-item-section-${i}`} />
                    <Input placeholder="Item" value={r.name} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="rounded-sm flex-1 min-w-[120px]" data-testid={`inv-item-${i}`} />
                    <Input placeholder="Unit" value={r.unit || ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} className="rounded-sm w-16" data-testid={`inv-item-unit-${i}`} />
                    <Input placeholder="Rate" type="number" value={r.price} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} className="rounded-sm w-24" data-testid={`inv-item-price-${i}`} />
                    <Input placeholder="Qty" type="number" value={r.qty} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} className="rounded-sm w-16" data-testid={`inv-item-qty-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="shrink-0" data-testid={`remove-line-${i}`}><Trash2 size={14} /></Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => setRows([...rows, { name: "", price: "", qty: 1 }])} className="rounded-sm mt-2">+ Add line</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase">Installation (₹)</Label><Input type="number" value={form.installation_charge} onChange={(e) => setForm({ ...form, installation_charge: e.target.value })} className="rounded-sm mt-1" data-testid="inv-installation" /></div>
              <div><Label className="text-xs uppercase">Transportation (₹)</Label><Input type="number" value={form.transportation_charge} onChange={(e) => setForm({ ...form, transportation_charge: e.target.value })} className="rounded-sm mt-1" data-testid="inv-transportation" /></div>
            </div>
            <div className="bg-muted/50 rounded-sm p-3 text-sm space-y-1" data-testid="inv-preview">
              <div className="flex justify-between text-muted-foreground"><span>Items subtotal</span><span>₹{itemsSubtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Installation + Transportation</span><span>₹{extras.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between font-heading font-bold text-secondary pt-1 border-t border-border"><span>Total incl. 18% GST</span><span>₹{previewTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="save-invoice-btn">{editId ? "Save Changes" : "Create Quotation"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quotation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete quotation <b>{deleteTarget?.invoice_number}</b> for {deleteTarget?.client_name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-invoice">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-invoice">{deleting ? "Deleting…" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="reject-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reject Quotation?</DialogTitle>
            <DialogDescription>Let us know why you're rejecting <b>{rejectTarget?.invoice_number}</b> (optional) so we can send a better quote.</DialogDescription>
          </DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Price too high, wrong items, need revised quantities…" className="rounded-sm" data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="rounded-sm" data-testid="cancel-reject-invoice">Cancel</Button>
            <Button onClick={confirmReject} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-reject-invoice">Reject Quotation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)}>
        <DialogContent data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Quotation History</DialogTitle>
            <DialogDescription>{historyTarget?.invoice_number} — {historyTarget?.client_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm" data-testid="history-timeline">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
              <div><p className="font-medium">Sent</p><p className="text-muted-foreground text-xs">{fmtDate(historyTarget?.created_at)}</p></div>
            </div>
            {historyTarget?.viewed_at && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                <div><p className="font-medium">Viewed by client</p><p className="text-muted-foreground text-xs">{fmtDate(historyTarget.viewed_at)}</p></div>
              </div>
            )}
            {historyTarget?.reminder_sent_at && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                <div><p className="font-medium">Reminder sent</p><p className="text-muted-foreground text-xs">{fmtDate(historyTarget.reminder_sent_at)}</p></div>
              </div>
            )}
            {historyTarget?.client_status === "expired" && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-slate-400 mt-1.5 shrink-0" />
                <div><p className="font-medium">Expired (no response)</p><p className="text-muted-foreground text-xs">{fmtDate(historyTarget.expired_at)}</p></div>
              </div>
            )}
            {(historyTarget?.client_status === "accepted" || historyTarget?.client_status === "rejected") && (
              <div className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${historyTarget.client_status === "accepted" ? "bg-green-600" : "bg-red-600"}`} />
                <div>
                  <p className="font-medium">{historyTarget.client_status === "accepted" ? "Accepted by client" : "Rejected by client"}</p>
                  <p className="text-muted-foreground text-xs">{fmtDate(historyTarget.responded_at)}</p>
                  {historyTarget.client_status === "rejected" && historyTarget.reject_reason && (
                    <p className="text-muted-foreground text-xs italic mt-0.5">"{historyTarget.reject_reason}"</p>
                  )}
                </div>
              </div>
            )}
            {(!historyTarget?.viewed_at && (historyTarget?.client_status || "pending") === "pending") && (
              <p className="text-xs text-muted-foreground pl-5">Not yet viewed or responded to by the client.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
